import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';

interface EditableFileNameProps {
    documentUuid: string;
    initialFileName: string;
    onUpdate?: (newFileName: string) => void;
    editable?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

const EditableFileName: React.FC<EditableFileNameProps> = ({
    documentUuid,
    initialFileName,
    onUpdate,
    editable = true,
    className = '',
    style = {},
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [fileName, setFileName] = useState(initialFileName);
    const [editedFileName, setEditedFileName] = useState(initialFileName);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setFileName(initialFileName);
        setEditedFileName(initialFileName);
    }, [initialFileName]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const updateFileName = async () => {
        if (!editedFileName.trim()) {
            setIsEditing(false);
            setEditedFileName(fileName);
            return;
        }

        if (editedFileName === fileName) {
            setIsEditing(false);
            return;
        }

        try {
            await api.patch(`/api/v1/kit_documents/${documentUuid}/update_filename`, {
                filename: editedFileName,
            });
            setFileName(editedFileName);
            setIsEditing(false);
            if (onUpdate) {
                onUpdate(editedFileName);
            }
        } catch (error: any) {
            alert(error.response?.data?.error || error.message || 'Failed to update file name');
            setEditedFileName(fileName);
            setIsEditing(false);
        }
    };

    const handleDoubleClick = (e: React.MouseEvent | React.PointerEvent) => {
        if (!editable) return;
        e.preventDefault();
        e.stopPropagation();
        setIsEditing(true);
        setEditedFileName(fileName);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            updateFileName();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setEditedFileName(fileName);
        }
    };

    if (!editable) {
        return (
            <h3 className={className} style={style}>
                {fileName}
            </h3>
        );
    }
    const useDoubleTap = <T extends React.SyntheticEvent>(
        callback: (e: T) => void,
        delay = 300
        ) => {
        const lastTap = useRef(0);

        return (e: T) => {
            const now = Date.now();
            if (now - lastTap.current < delay) {
            callback(e);
            }
            lastTap.current = now;
        };
    };

    const onTap = useDoubleTap(handleDoubleClick);
    return (
        <>
            {isEditing ? (
                <div   className={className}
                    style={{ ...style}}
                ><input
                    ref={inputRef}
                    type="text"
                    value={editedFileName}
                    onChange={(e) => setEditedFileName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={updateFileName}
                    className='w-full'
                    // className={className}
                    // style={style}
                /></div>
            ) : (
                <h3
                    className={className}
                    style={{ ...style, cursor: 'pointer' }}
                    // onDoubleClick={handleDoubleClick}
                    onClick={onTap}
                    title="Double-click to edit file name"
                >
                    {fileName}
                </h3>
            )}
        </>
    );
};

export default EditableFileName;
